import React, { useMemo } from 'react'
import { FormikValues } from 'formik'
import { isNil } from 'lodash-es'

import { FormikStepper } from '../../../../components/form/wizzard/FormikStepper'
import { useFormDialogContext } from '../../../../components/layout/FormDialog'
import { useAppTranslations } from '../../../../utils/hooks/useAppTranslations'
import prepareFormValues from '../../../../utils/prepareFormValues'
import Loader from '../../../../components/loader/Loader'
import { BudgetCapping, useAdvertiserConfigQuery } from '../../../../__generated__/graphql'
import { useCompanyDetailsContext } from '../../../../utils/hooks/useCompanyDetailsContext'
import { useUserPermissions } from '../../../company/userPermissions'
import AdgroupDetailStep, { detailsSchemaWithoutCampaign } from '../../../adgroup/create/AdgroupDetailStep'
import AdgroupBudgetStep from '../../../adgroup/create/AdgroupBudgetStep'
import {
  buildAdgroupCreateFormInitialValues,
  buildBaseAdgroupCreateInput,
} from '../../../adgroup/create/buildAdgroupCreateInitialValues'
import { budgetAndDateSchema } from '../../../adgroup/schema'
import { CampaignCreateAdgroupDraft, createAdgroupDraft } from './campaignCreateDrafts'
import { getOpportunityBudgetPrefill } from './campaignCopperBudget.utils'

interface CampaignCreateAdgroupModalProps {
  onAdd: (adgroup: CampaignCreateAdgroupDraft) => void
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  opportunityBudgetRemaining: number
}

export const CampaignCreateAdgroupModal: React.FC<CampaignCreateAdgroupModalProps> = ({
  onAdd,
  setOpen,
  opportunityBudgetRemaining,
}) => {
  const [, setHasChanged] = useFormDialogContext()
  const t = useAppTranslations()
  const { adgroupDatesRequired } = useUserPermissions()
  const [error, setError] = React.useState(undefined)
  const {
    company: { id: companyId },
  } = useCompanyDetailsContext()
  const { data: advertiserConfig, loading: advertiserConfigLoading } = useAdvertiserConfigQuery({
    variables: { id: companyId },
  })

  const baseAdgroup = useMemo(
    () => (advertiserConfig ? buildBaseAdgroupCreateInput(undefined, advertiserConfig.advertiser.config, []) : null),
    [advertiserConfig]
  )

  const initialValues = useMemo(() => {
    if (!baseAdgroup) return null
    const formValues = buildAdgroupCreateFormInitialValues(baseAdgroup)
    const prefill = getOpportunityBudgetPrefill(
      opportunityBudgetRemaining,
      Number(advertiserConfig?.advertiser.config.adGroup.max.budgetCapping) || 0
    )
    if (prefill == null) return formValues
    return {
      ...formValues,
      budgetCapping: prefill,
      budgetCappingType: BudgetCapping.Period,
    }
  }, [baseAdgroup, opportunityBudgetRemaining, advertiserConfig])

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    onAdd(createAdgroupDraft(prepareFormValues(values) as Omit<CampaignCreateAdgroupDraft, 'draftId'>))
  }

  if (advertiserConfigLoading || isNil(advertiserConfig) || !baseAdgroup || !initialValues) return <Loader />

  return (
    <FormikStepper
      initialValues={initialValues}
      onSubmit={handleSubmit}
      error={error}
      setError={setError}
      enableReinitialize={true}
      successMessage={t('adgroup.created')}
      autoCloseOnSuccess={true}
      setOpen={setOpen}
      setHasChanged={setHasChanged}
      mergeSteps
      validateOnChange={false}
    >
      <AdgroupDetailStep
        validationSchema={detailsSchemaWithoutCampaign}
        label={t('adgroup.details.title')}
        adgroup={baseAdgroup}
        priorityList={[]}
        hideCampaignSelector={true}
      />
      <AdgroupBudgetStep
        validationSchema={budgetAndDateSchema(advertiserConfig.advertiser.config.adGroup, {
          adgroupDatesRequired,
        })}
        label={t('adgroup.budgetAndDates.title')}
        adgroup={baseAdgroup}
      />
    </FormikStepper>
  )
}
